import { UIOrderType, UIOrderTypeValue } from '@velocity-exchange/common';
import { partition } from 'lodash';
import { memo, useMemo } from 'react';
import useTabsWithSelect from 'src/components/primitives/Tabs/hooks/useTabsWithSelect';
import Tabs from 'src/components/primitives/Tabs/Tabs';
import { useWindowSizeIsLessThan } from 'src/hooks/useWindowSize';

interface Props {
	currentOrderType: UIOrderType;
	orderTypeOptions: UIOrderTypeValue[];
	onOrderTypeChange: (orderType: UIOrderType) => void;
}

function OrderTypeTabs({
	currentOrderType,
	orderTypeOptions,
	onOrderTypeChange,
}: Props) {
	const isLessThanXL = useWindowSizeIsLessThan('xl');

	const [marketOrLimitOptions, otherOptions] = useMemo(
		() =>
			partition(
				orderTypeOptions,
				(option) => option.value === 'market' || option.value === 'limit'
			),
		[orderTypeOptions]
	);

	const selectItems = useMemo(
		() =>
			otherOptions.map((option) => ({
				label: option.label,
				value: option.value,
				selectId: option.value,
			})),
		[otherOptions]
	);

	const { activeTab, tabOptionsWithSelect, handleTabChange } =
		useTabsWithSelect({
			value: currentOrderType,
			tabOptions: marketOrLimitOptions,
			selectItems,
			selectPlaceholder: 'Others',
			onValueChange: onOrderTypeChange,
		});

	return (
		<Tabs
			className="gap-x-xs self-start"
			size={isLessThanXL ? 'sm' : 'md'}
			variant="secondary"
			activeTab={activeTab}
			options={tabOptionsWithSelect}
			onTabChange={handleTabChange}
			dataTestId="trade-order-type-tabs"
			tabTestIdPrefix="order-type"
			indicatorClassName="rounded-full"
		/>
	);
}

export default memo(OrderTypeTabs);
